import React, { useState } from 'react';
import { Twitter, Linkedin, Github } from 'lucide-react';
import { APP_CONFIG, FOOTER_NAV } from '@/lib/constants';
import { cn } from '@/lib/utils';
import type { FooterProps } from '@/types';
import { SupportOptionsModal } from '@/components/support/SupportOptionsModal';

/**
 * Footer Component
 * Site footer with navigation links, social media, and company info
 */
export function Footer({ className }: FooterProps) {
  // Remove unused parameters navItems and socialLinks
  const currentYear = new Date().getFullYear();
  const [showSupportOptionsModal, setShowSupportOptionsModal] = useState(false);

  return (
    <footer className={cn("border-t border-border/10 bg-gradient-card", className)}>
      <div className="container mx-auto px-4 py-12">
        {/* Main Footer Content */}
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <div className="flex items-center">
              <img
                src="/images/logo.png"
                alt="NeurosEdge Logo"
                className="h-10 w-auto object-contain"
              />
            </div>
            <p className="text-muted-foreground text-sm">
              Advanced AI automation for DevOps and cybersecurity professionals.
            </p>

            <div>
              <p className="text-muted-foreground text-sm">
                We don’t just execute. We adapt. NeurosEdge is the bridge from scale to adaptation.
              </p>
            </div>

            {/* <div className="flex space-x-4">
              <Twitter className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
              <Linkedin className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
              <Github className="w-5 h-5 text-muted-foreground hover:text-primary cursor-pointer transition-colors" />
            </div> */}
          </div>
          
          {/* Product Links */}
          <FooterSection title="Product" links={FOOTER_NAV.product} />
          
          {/* Resources Links */}
          <FooterSection
            title="Resources"
            links={FOOTER_NAV.resources}
            onSupportClick={() => setShowSupportOptionsModal(true)}
          />
          
          {/* Company Links */}
          <FooterSection title="Company" links={FOOTER_NAV.company} />
        </div>
        
        {/* Footer Bottom */}
        <div className="border-t border-border/10 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-sm text-muted-foreground">
              © {currentYear} {APP_CONFIG.name}, Inc. All rights reserved.
            </p>
            <div className="flex space-x-6 text-sm text-muted-foreground">
              {FOOTER_NAV.legal.map((link) => (
                <a 
                  key={link.label}
                  href={link.href} 
                  className="hover:text-foreground transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Support Options Modal */}
      <SupportOptionsModal
        open={showSupportOptionsModal}
        onOpenChange={setShowSupportOptionsModal}
      />
    </footer>
  );
}

/**
 * Footer Section Component
 * Reusable section for footer navigation groups
 */
interface FooterSectionProps {
  title: string;
  links: readonly { readonly label: string; readonly href: string }[];
  onSupportClick?: () => void;
}

function FooterSection({ title, links, onSupportClick }: FooterSectionProps) {
  const handleLinkClick = (e: React.MouseEvent, link: { label: string; href: string }) => {
    if (link.label === 'Support' && onSupportClick) {
      e.preventDefault();
      onSupportClick();
    }
  };

  return (
    <div>
      <h4 className="font-semibold mb-4">{title}</h4>
      <ul className="space-y-2 text-sm text-muted-foreground">
        {links.map((link) => (
          <li key={link.label}>
            <a
              href={link.href}
              className="hover:text-foreground transition-colors cursor-pointer"
              onClick={(e) => handleLinkClick(e, link)}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
